import { useDispatch } from "react-redux";
import { showAuthModal } from "store/application/application-action";
import { useAddLikeMutation, useRemoveLikeMutation } from "queries";
import { useCallback, useState, useEffect } from "react";
import { useLikedGifs } from "hooks";
import { metrics } from "utils/metrics";
import { setAnonymousLikedToast, loadAnonymousLikedToast } from "utils/storage";
import { toast } from "react-hot-toast";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";
import { CurrentUserObject } from "hooks/useVisitorInfo";
import { applyAnimation, AnimationClass } from "components/player/Player/utils/helpers";

interface IUseLikeActionsProps {
  gifId: string;
  source: string | null;
  initialLikesCount: number;
  ref: React.RefObject<HTMLDivElement>;
  currentUser: CurrentUserObject | null;
}

const STATUS_401 = 401;

export const useLikeActions = ({ gifId, source, initialLikesCount, ref, currentUser }: IUseLikeActionsProps) => {
  const dispatch = useDispatch();
  const likes = useLikedGifs();

  const [liked, setLiked] = useState<boolean>(false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);

  const addLikeMutation = useAddLikeMutation();
  const removeLikeMutation = useRemoveLikeMutation();

  const showToast = useCallback(() => {
    if (!currentUser && !loadAnonymousLikedToast()) {
      toast.success(
        "Please log in to make sure your liked gifs aren’t lost",
        { duration: 5000 }
      );
      setAnonymousLikedToast(true);
    }
  }, [currentUser]);

  const handleUnauthorized = useCallback((e: AxiosError<IErrorData>) => {
    if (e?.response?.status === STATUS_401) {
      dispatch(showAuthModal());
    }
  }, [dispatch]);

  const handleAddLike = useCallback(async () => {
    setLiked(true);
    setLikesCount((prev) => prev + 1);
    applyAnimation({ ref, animationClass: AnimationClass.Liked });

    try {
      await addLikeMutation.mutateAsync({ gifId, source });
      await metrics.sendLiked({ gif: gifId });
      showToast();
      console.debug(`Gif ${gifId} liked.`);
    } catch (e) {
      console.error(`Gif ${gifId} could not be liked: ${e}.`);
      setLiked(false);
      setLikesCount((prev) => prev - 1);
      handleUnauthorized(e as AxiosError<IErrorData>);
    }
  }, [gifId, source, showToast, addLikeMutation, handleUnauthorized, ref]);

  const handleRemoveLike = useCallback(async () => {
    setLikesCount((prev) => prev - 1);
    setLiked(false);
    applyAnimation({ ref, animationClass: AnimationClass.Unlike });

    try {
      await removeLikeMutation.mutateAsync({ gifId, source });
      console.debug(`Gif ${gifId} unliked.`);
    } catch (e) {
      console.error(`Gif ${gifId} could not be unliked: ${e}.`);
      setLiked(true);
      setLikesCount((prev) => prev + 1);
      handleUnauthorized(e as AxiosError<IErrorData>);
    }
  }, [gifId, source, removeLikeMutation, handleUnauthorized, ref]);

  const handleLike = useCallback(() => {
    if (liked) {
      handleRemoveLike();
    } else {
      handleAddLike();
    }
  }, [liked, handleRemoveLike, handleAddLike]);

  useEffect(() => {
    setLiked(likes.indexOf(gifId) >= 0);
  }, [gifId, likes]);

  return {
    liked,
    likesCount,
    handleLike,
  };
};
